import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teacherCode?: string;
  initial?: any;
}

export function FixedJobFormDialog({ open, onOpenChange, teacherCode, initial }: Props) {
  const qc = useQueryClient();
  const [subject, setSubject] = useState('');
  const [location, setLocation] = useState('');
  const [weekday, setWeekday] = useState<number>(1);
  const [startTime, setStartTime] = useState('16:00');
  const [endTime, setEndTime] = useState('17:00');
  const [isActive, setIsActive] = useState(true);
  const [studentsText, setStudentsText] = useState('');

  useEffect(() => {
    if (initial) {
      setSubject(initial.subject ?? '');
      setLocation(initial.location ?? '');
      setWeekday(initial.weekday ?? 1);
      setStartTime((initial.start_time ?? '16:00').slice(0,5));
      setEndTime((initial.end_time ?? '17:00').slice(0,5));
      setIsActive(Boolean(initial.is_active ?? true));
      const s = Array.isArray(initial.students) ? initial.students.map((x: any) => x.code).join(',') : '';
      setStudentsText(s);
    } else {
      setSubject(''); setLocation(''); setWeekday(1); setStartTime('16:00'); setEndTime('17:00'); setIsActive(true); setStudentsText('');
    }
  }, [initial, open]);

  const upsertMutation = useMutation({
    mutationFn: async () => {
      if (!teacherCode) throw new Error('กรุณาระบุ teacher_code');
      let fixedJobId = initial?.id as string | undefined;
      if (fixedJobId) {
        const { error } = await (supabase as any)
          .from('fixed_jobs')
          .update({ subject, location, weekday, start_time: startTime, end_time: endTime, is_active: isActive })
          .eq('id', fixedJobId);
        if (error) throw error;
      } else {
        const { data, error } = await (supabase as any)
          .from('fixed_jobs')
          .insert({ teacher_code: teacherCode, subject, location, weekday, start_time: startTime, end_time: endTime, is_active: isActive })
          .select('id')
          .single();
        if (error) throw error;
        fixedJobId = data.id as string;
      }

      // Sync students
      const desired = studentsText.split(',').map(s => s.trim()).filter(Boolean);
      // Remove all then re-insert for simplicity
      const { error: delErr } = await (supabase as any)
        .from('fixed_job_students')
        .delete()
        .eq('fixed_job_id', fixedJobId);
      if (delErr) throw delErr;
      if (desired.length > 0) {
        const rows = desired.map(id => ({ fixed_job_id: fixedJobId, student_id: id }));
        const { error: insErr } = await (supabase as any)
          .from('fixed_job_students')
          .insert(rows);
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-fixed-jobs'] });
      onOpenChange(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? 'แก้ไข Fixed Job' : 'เพิ่ม Fixed Job'}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label>วิชา</Label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <Label>สถานที่</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} />
          </div>
          <div>
            <Label>วัน (0=อา ... 6=ส)</Label>
            <Input type="number" min={0} max={6} value={weekday} onChange={(e) => setWeekday(parseInt(e.target.value || '0', 10))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>เริ่ม</Label>
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div>
              <Label>สิ้นสุด</Label>
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>
          <div>
            <Label>นักเรียน (คั่นด้วย , รหัส student_id)</Label>
            <Input placeholder="STU001,STU002" value={studentsText} onChange={(e) => setStudentsText(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>ยกเลิก</Button>
          <Button onClick={() => upsertMutation.mutate()} disabled={upsertMutation.isPending}>
            {initial ? 'บันทึก' : 'สร้าง'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


